import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Trash2 } from "lucide-react";

const FIELDS = [
  { key: "waist", label: "Waist" },
  { key: "hips", label: "Hips" },
  { key: "chest", label: "Chest" },
  { key: "arms", label: "Arms" },
  { key: "thighs", label: "Thighs" },
  { key: "calves", label: "Calves" },
];

const COLORS = ["#1F1B15", "#93673D", "#7A5F49", "#4A4238", "#B39072", "#8A7A5F"];

export default function Measurements() {
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState({});
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [visible, setVisible] = useState({ waist: true, hips: true, chest: true, arms: false, thighs: false, calves: false });

  const load = async () => {
    const { data } = await api.get("/measurements");
    setEntries(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const anyFilled = FIELDS.some((f) => form[f.key] !== undefined && form[f.key] !== "");
    if (!anyFilled) {
      toast.error("Add at least one measurement.");
      return;
    }
    setSaving(true);
    try {
      const payload = { note };
      FIELDS.forEach((f) => { if (form[f.key]) payload[f.key] = parseFloat(form[f.key]); });
      await api.post("/measurements", payload);
      setForm({}); setNote("");
      await load();
      toast.success("Logged. Look at you, showing up.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/measurements/${id}`);
    setEntries((e) => e.filter((x) => x.id !== id));
  };

  const chartData = entries.map((e, i) => ({
    label: new Date(e.recorded_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    ...FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: e[f.key] }), {}),
  }));

  return (
    <div className="space-y-14" data-testid="measurements-root">
      <header>
        <div className="label-eyebrow mb-3">Measurements</div>
        <h1 className="font-serif text-5xl md:text-7xl leading-[1] tracking-tight">Your body, <em className="italic text-[hsl(var(--primary))]">as landscape</em>.</h1>
        <p className="mt-6 text-stone-600 max-w-xl leading-relaxed">No scale. Six honest zones. Watch your shape shift over 4 weeks.</p>
      </header>

      <section className="grid md:grid-cols-12 gap-0 border-t border-b border-border">
        <form onSubmit={submit} className="md:col-span-5 p-8 md:p-10 border-r border-border space-y-5" data-testid="measurements-form">
          <div className="label-eyebrow">Log entry (cm or inches — your call)</div>
          <div className="grid grid-cols-2 gap-4">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <label className="label-eyebrow block mb-2">{f.label}</label>
                <input
                  type="number" step="0.1"
                  value={form[f.key] || ""}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  data-testid={`input-${f.key}`}
                  className="w-full bg-transparent border-0 border-b border-border py-2 no-shadow-focus text-lg"
                  placeholder="—"
                />
              </div>
            ))}
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Note (optional)</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="input-note" className="w-full bg-transparent border-0 border-b border-border py-2 no-shadow-focus" placeholder="How did you feel today?" />
          </div>
          <button type="submit" disabled={saving} data-testid="btn-log-measurement" className="bg-[hsl(var(--primary))] text-white px-6 py-3 hover:bg-[hsl(var(--foreground))] transition-colors disabled:opacity-60">
            {saving ? "Logging…" : "Log entry"}
          </button>
        </form>

        <div className="md:col-span-7 p-8 md:p-10">
          <div className="label-eyebrow mb-4">Over time</div>
          {entries.length === 0 ? (
            <p className="text-stone-500 italic font-serif text-xl">No entries yet — begin with the form on the left.</p>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 mb-6" data-testid="chart-legend">
                {FIELDS.map((f, i) => (
                  <button key={f.key} onClick={() => setVisible({ ...visible, [f.key]: !visible[f.key] })} className={`text-xs px-3 py-1 border transition-colors ${visible[f.key] ? "border-[hsl(var(--foreground))]" : "border-border text-stone-400"}`} style={visible[f.key] ? { color: COLORS[i] } : {}}>
                    <span className="inline-block w-2 h-2 mr-2 align-middle" style={{ background: COLORS[i] }} />{f.label}
                  </button>
                ))}
              </div>
              <div className="h-64">
                <ResponsiveContainer>
                  <LineChart data={chartData}>
                    <XAxis dataKey="label" stroke="#78716c" fontSize={11} axisLine={false} tickLine={false} />
                    <YAxis stroke="#78716c" fontSize={11} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E5DFD3", fontFamily: "Manrope" }} />
                    {FIELDS.map((f, i) => visible[f.key] && (
                      <Line key={f.key} type="monotone" dataKey={f.key} stroke={COLORS[i]} strokeWidth={1.5} dot={{ r: 3, fill: COLORS[i] }} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>
      </section>

      <section>
        <div className="label-eyebrow mb-4">All entries</div>
        {entries.length === 0 ? <p className="text-stone-500 italic">Nothing logged yet.</p> : (
          <table className="w-full text-sm border-t border-border" data-testid="entries-table">
            <thead>
              <tr className="text-left text-stone-500">
                <th className="py-3 label-eyebrow">Date</th>
                {FIELDS.map((f) => <th key={f.key} className="py-3 label-eyebrow">{f.label}</th>)}
                <th className="py-3 label-eyebrow">Note</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {[...entries].reverse().map((e) => (
                <tr key={e.id} className="border-t border-border">
                  <td className="py-3">{new Date(e.recorded_at).toLocaleDateString()}</td>
                  {FIELDS.map((f) => <td key={f.key} className="py-3">{e[f.key] ?? "—"}</td>)}
                  <td className="py-3 text-stone-500 italic">{e.note || "—"}</td>
                  <td className="py-3 text-right">
                    <button onClick={() => remove(e.id)} data-testid={`delete-${e.id}`} className="text-stone-400 hover:text-[hsl(var(--destructive))]"><Trash2 className="w-4 h-4" strokeWidth={1.25} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
